/**
 * Sleep timer — pure logic, kept separate from AudioPlayerContext so it can
 * be unit-tested (see src/lib/sleepTimer.test.ts) without mounting a real
 * <audio> element.
 *
 * Modes, per the spec:
 *  - "off": no timer.
 *  - "end_of_chapter": stop at the end of the CURRENT chapter (needs the
 *    track's chapter list + current position).
 *  - "end_of_story": stop when the track itself ends (no explicit deadline
 *    to compute — the caller just lets `ended` fire and doesn't loop/advance).
 *  - "10" | "20" | "30": stop N minutes from when the mode was set.
 */
export type SleepTimerMode = "off" | "end_of_chapter" | "end_of_story" | "10" | "20" | "30";

export interface SleepTimerChapter {
  /** Seconds from the start of the track. */
  startS: number;
}

/** Given the mode and (for minute-based modes) the moment it was armed,
 *  returns the wall-clock epoch ms at which playback should pause, or null
 *  if this mode has no fixed deadline (off / end_of_chapter / end_of_story
 *  are resolved differently — see shouldPauseNow below). */
export function deadlineForMode(mode: SleepTimerMode, armedAtMs: number): number | null {
  const minutes = mode === "10" ? 10 : mode === "20" ? 20 : mode === "30" ? 30 : null;
  if (minutes === null) return null;
  return armedAtMs + minutes * 60_000;
}

/** For "end_of_chapter": given the sorted chapter start times (seconds) and
 *  the current playback position (seconds), returns the position (seconds)
 *  at which playback should pause — the start of the NEXT chapter, or null
 *  if the current chapter is already the last one (falls back to "end of
 *  story" behaviour: let it play to the end). */
export function endOfCurrentChapterS(
  chapters: SleepTimerChapter[],
  positionS: number,
): number | null {
  const sorted = [...chapters].map((c) => c.startS).sort((a, b) => a - b);
  const next = sorted.find((startS) => startS > positionS + 0.25);
  return next ?? null;
}

/** Central decision function used by the player's timeupdate handler.
 *  Returns true when playback should pause right now. */
export function shouldPauseNow(params: {
  mode: SleepTimerMode;
  nowMs: number;
  deadlineMs: number | null;
  positionS: number;
  chapterBoundaryS: number | null;
}): boolean {
  const { mode, nowMs, deadlineMs, positionS, chapterBoundaryS } = params;
  if (mode === "off" || mode === "end_of_story") return false;
  if (mode === "end_of_chapter") {
    return chapterBoundaryS !== null && positionS >= chapterBoundaryS;
  }
  return deadlineMs !== null && nowMs >= deadlineMs;
}

export const SLEEP_TIMER_MODES: SleepTimerMode[] = [
  "off",
  "end_of_chapter",
  "end_of_story",
  "10",
  "20",
  "30",
];
